import IconHorizontalRebrand from '@/assets/logos/icon-horizontal-svg/icon-horizontal-rebrand.svg';
import { useQueryWebinarTimeslots } from '@/hooks/query/useQueryWebinarTimeslots';
import { isOnPrem } from '@/utils/buildProfile';
import { type WebinarRegion } from '@/utils/onboardingExperience';
import { submitWebinarSignup } from '@/utils/webinarSignup';
import { Button } from '@heidi/design-system/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import {
  TypographyH2,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { type LanguageCode } from '@heidi/schema/ml-scribe';
import { Loader } from '@heidi/snowflake-ui/loader';
import { type CountryType } from '@heidi/utils/countries';
import { ArrowLeft, Calendar } from 'lucide-react';
import Image from 'next/image';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { deriveNameFromEmail } from './book-demo-calendar-step.utils';
import { DemoCountryLanguageFields } from './demo-country-language-fields';

interface WebinarTimeslot {
  value: string;
  label: string;
  hubspotProperty: string;
}

interface BookDemoWebinarVariantProps {
  email: string;
  region: WebinarRegion;
  country: CountryType | null;
  onCountryChange: (country: CountryType) => void;
  languageCode: string | null;
  onLanguageChange: (language: string) => void;
  showLanguage: boolean;
  availableLanguages: LanguageCode[];
  onSubmitted: () => void;
  onBack: () => void;
  onTrackSubmitted: (region: WebinarRegion, timeslotLabel: string) => void;
}

export function BookDemoWebinarVariant({
  email,
  region,
  country,
  onCountryChange,
  languageCode,
  onLanguageChange,
  showLanguage,
  availableLanguages,
  onSubmitted,
  onBack,
  onTrackSubmitted,
}: BookDemoWebinarVariantProps) {
  const [selected, setSelected] = useState<WebinarTimeslot>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const { data: timeslots, isLoading } = useQueryWebinarTimeslots(region);

  const items = useMemo(
    () =>
      (timeslots ?? []).map((slot) => ({
        value: slot.value,
        label: slot.label,
        hubspotProperty: slot.hubspotProperty,
      })),
    [timeslots]
  );

  const handleSelectTimeslot = (value: string) => {
    const next = items.find((item) => item.value === value);
    if (next) setSelected(next);
  };

  const handleSubmit = useCallback(async () => {
    if (!selected || isSubmitting || !email.trim()) return;

    // ON-PREM-GATE: belt-and-braces per F1 — block HubSpot submission even if
    // PostHog (which drives country-config) leaked into an on-prem build.
    if (isOnPrem) {
      onSubmitted();
      return;
    }

    setIsSubmitting(true);
    setSubmitError(false);
    onTrackSubmitted(region, selected.label);
    const { firstName, lastName } = deriveNameFromEmail(email);
    try {
      const ok = await submitWebinarSignup({
        firstName,
        lastName,
        email,
        timeslot: selected,
      });
      if (ok) {
        onSubmitted();
      } else {
        setSubmitError(true);
      }
    } finally {
      // Reset on success and failure so a thrown error doesn't strand the
      // Register button in a permanently-disabled state.
      setIsSubmitting(false);
    }
  }, [selected, isSubmitting, email, onTrackSubmitted, region, onSubmitted]);

  return (
    <div className="relative z-10 flex w-full flex-col items-center gap-10 px-6 py-8">
      <div className="flex flex-col items-center gap-6">
        <Image
          src={IconHorizontalRebrand}
          alt="Heidi"
          width={80}
          height={24}
          className="dark-invert"
        />
        <div className="flex flex-col items-center gap-1 text-center">
          <TypographyH2>
            <FormattedMessage id="5XDTsd1t1x" defaultMessage="Join a Heidi webinar" />
          </TypographyH2>
          <TypographyP2 className="text-text-secondary">
            <FormattedMessage
              id="H9APmSW5od"
              defaultMessage="Pick a session that suits your schedule."
            />
          </TypographyP2>
        </div>
      </div>

      <div className="flex w-full max-w-[400px] flex-col gap-4 rounded-2xl bg-surface p-6 shadow-lg">
        <DemoCountryLanguageFields
          country={country}
          onCountryChange={onCountryChange}
          languageCode={languageCode}
          onLanguageChange={onLanguageChange}
          showLanguage={showLanguage}
          availableLanguages={availableLanguages}
        />

        <div className="flex flex-col gap-1">
          <TypographyP2Bold id="webinar-timeslot-label" className="leading-tight">
            <FormattedMessage id="h/FgU80uT9" defaultMessage="Pick a timeslot" />
          </TypographyP2Bold>
          <Select
            value={selected?.value ?? ''}
            onValueChange={handleSelectTimeslot}
            disabled={isLoading || items.length === 0}
            aria-labelledby="webinar-timeslot-label"
          >
            <SelectTrigger data-testid="webinar-timeslot-select">
              <Calendar size={16} className="me-1 text-text-secondary" />
              <SelectValue
                placeholder={
                  isLoading ? (
                    <FormattedMessage id="T4VxQNSBvZ" defaultMessage="Loading…" />
                  ) : (
                    <FormattedMessage
                      id="6RjHG9pUE1"
                      defaultMessage="Select a timeslot"
                    />
                  )
                }
              />
            </SelectTrigger>
            <SelectContent>
              {items.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {submitError && (
          <TypographyP2
            role="alert"
            aria-live="polite"
            className="text-center text-validation-destructive"
            data-testid="webinar-submit-error"
          >
            <FormattedMessage
              id="W/5hwrn09m"
              defaultMessage="Something went wrong. Please try again."
            />
          </TypographyP2>
        )}

        <Button
          size="lg"
          disabled={!selected || isSubmitting}
          onClick={() => void handleSubmit()}
          data-testid="webinar-submit-button"
        >
          {isSubmitting ? (
            <Loader className="size-5 text-text-primary-invert" />
          ) : (
            <FormattedMessage id="deEeEIe6bj" defaultMessage="Register" />
          )}
        </Button>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="gap-1.5 text-text-secondary"
      >
        <ArrowLeft size={16} className="rtl:-scale-x-100" />
        <FormattedMessage id="cyR7KhiuaU" defaultMessage="Back" />
      </Button>
    </div>
  );
}
